/**
 * sessionContext — holds the current user/org snapshot from GET /v1/me and the
 * live role (ADMIN/EDITOR/VIEWER) used for permission gating in the UI.
 * The design lets you cycle the role; that is reflected here.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type Role = 'ADMIN' | 'EDITOR' | 'VIEWER';
const ROLES: Role[] = ['ADMIN', 'EDITOR', 'VIEWER'];

export interface SessionUser {
  firstName: string;
  lastName: string;
  email: string;
}
export interface SessionOrg {
  id: string;
  name: string;
}

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: Role;
  setSession: (user: SessionUser | null, org: SessionOrg | null) => void;
  cycleRole: () => void;
  setRole: (r: Role) => void;
  clear: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRoleState] = useState<Role>('ADMIN');

  const setSession = useCallback((u: SessionUser | null, o: SessionOrg | null) => {
    setUser(u);
    setOrg(o);
  }, []);
  const cycleRole = useCallback(() => {
    setRoleState((r) => ROLES[(ROLES.indexOf(r) + 1) % ROLES.length]);
  }, []);
  const clear = useCallback(() => {
    setUser(null);
    setOrg(null);
    setRoleState('ADMIN');
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ user, org, role, setSession, cycleRole, setRole: setRoleState, clear }),
    [user, org, role, setSession, cycleRole, clear],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
